import './index.less';
import { Alert, AlertProps, message } from 'antd';
import {
  CloseIcon,
  MessageErrorIcon,
  MessageInfoIcon,
  MessageSuccessIcon,
  MessageWarningIcon,
} from 'components/Icons';
import classNames from 'classnames';
import { isValidElement, ReactNode } from 'react';
import { uniqueId } from 'lodash-es';

export type RevxMessageProps = AlertProps;

export const TOAST_TYPE = {
  info: 'info',
  error: 'error',
} as const;
type ValueOf<T> = T[keyof T];
export type ToastType = ValueOf<typeof TOAST_TYPE>;

export const RevxMessage = (props: RevxMessageProps) => {
  const iconMap = {
    success: <MessageSuccessIcon />,
    error: <MessageErrorIcon />,
    warning: <MessageWarningIcon />,
    info: <MessageInfoIcon />,
  };

  return (
    <Alert
      className={classNames('revx-alert')}
      {...props}
      icon={props.type ? iconMap[props.type] : undefined}
      closeText={props.closable ? <CloseIcon /> : null}
    ></Alert>
  );
};

// Callers sometimes pass an Error (or an object) instead of a message; React
// throws on those, which turns a failed request into a blank screen.
const toMessageNode = (msg: string | ReactNode): ReactNode => {
  if (
    msg == null ||
    typeof msg === 'string' ||
    typeof msg === 'number' ||
    typeof msg === 'boolean' ||
    Array.isArray(msg) ||
    isValidElement(msg)
  ) {
    return msg;
  }
  if (msg instanceof Error) {
    return msg.message;
  }
  try {
    return String(msg);
  } catch {
    return 'Something went wrong.';
  }
};

export const toast = function (
  msg: string | ReactNode,
  type: ToastType = TOAST_TYPE.info,
  closeHandle?: () => void
) {
  const uniqId = uniqueId('toast');
  message[type]({
    content: (
      <div className="toast-container">
        {type === TOAST_TYPE.error && <MessageErrorIcon />}
        <span className="toast-container__msg">{toMessageNode(msg)}</span>
      </div>
    ),
    duration: 5,
    className: classNames('toast', type),
    icon: (
      <span
        onClick={() => {
          if (closeHandle) {
            closeHandle();
          }
          message.destroy(uniqId);
        }}
      >
        <CloseIcon className="close" />
      </span>
    ),
    key: uniqId,
    onClose: () => {
      if (closeHandle) {
        closeHandle();
      }
    },
  });
};
toast.error = (msg: string) => toast(msg, TOAST_TYPE.error);

export const error = message.error;
export const info = message.info;
export const success = message.success;
export const warning = message.warning;
